import { calculateBottomCenter, ICoordinate, IRectangle } from '@opengov/data-viz';
import { BasicColumnTypes, magicNotApplicableNumber, magicNotApplicableString } from '@opengov/ogql-types';
import { calculateColumnAmountType } from '@opengov/reporting-formatters/formatters';
import { EReportColumnTypes, IReportConfiguration, OG_META_DATA_KEY } from '@opengov/reporting-types';

const positionTooltip =
  (calculateOffsets) =>
  (xFocalPoint: number, yFocalPoint: number, viewPort: IRectangle, tooltipRect: IRectangle): ICoordinate => {
    const [xOffset, yOffset] = calculateOffsets(tooltipRect.width, tooltipRect.height);

    return {
      x: xFocalPoint - xOffset - viewPort.left,
      y: yFocalPoint - yOffset - viewPort.top
    };
  };

export type IGraphCallbackCategoryData = {
  id: string;
  name: string;
  [key: string]: any;
  [OG_META_DATA_KEY]: {
    // These are actually string representations of numbers
    // but TS can't infer from these dynamic keys and also requires they be type-annotated
    // hence they are marked as `any`
    percentTotal: { [key: string]: any };
    prevChange: { [key: string]: any };
    total: { [key: string]: any };
    value: { [key: string]: any };
    targetStatus?: string;
  };
};

export type ILineGraphCallbackData = {
  category: string;
  id: string;
  value: number;
  x: number;
  y0: number;
  y1: number;
  data: IGraphCallbackCategoryData;
};

export const positionTooltipBottomCenter = positionTooltip(calculateBottomCenter);

export const formatChangeValue = (
  meta: IGraphCallbackCategoryData['OG_META_OG'],
  category: string,
  percentFormatter: (p: { value: number }) => string,
  notApplicableText: string
): string => {
  if (meta.prevChange && meta.prevChange[category]) {
    // magicNotApplicableString is the marker for divide by zero and other
    // accountant things. Any 0 in the previous or current value will return this
    if (
      meta.prevChange[category] === magicNotApplicableString ||
      meta.prevChange[category] === magicNotApplicableNumber
    ) {
      return notApplicableText;
    } else {
      return percentFormatter({ value: parseFloat(meta.prevChange[category]) });
    }
  }

  return null;
};

// the query will return the % change for the previous point even if
// there are gaps.  This allows us to not show a % change from a gap
export const hasPreviousData =
  (allData: [{ id }]) =>
  (id, category: string): boolean => {
    // sometimes id is a string ... sometimes
    const currentIdx = allData.findIndex((d) => `${d.id}` === `${id}`);
    if (currentIdx === 0) {
      return false;
    }

    return Object.prototype.hasOwnProperty.call(allData[currentIdx - 1], category);
  };

export const findAmountFormatter = (
  config: IReportConfiguration,
  columnFormatter: (column: any) => (p: { value: number }) => string
) => {
  const amountColumn =
    config.detailTableQuery.columns &&
    config.detailTableQuery.columns.find((c) => c.name === config.visualizationQuery.showColumn.name);

  let amountType;
  if (amountColumn) {
    amountType = calculateColumnAmountType(amountColumn);
  } else {
    if (config.visualizationQuery.showColumn.columnType === EReportColumnTypes.percentOfTotal) {
      amountType = config.visualizationQuery.showColumn.originalColumnType;
    } else if (config.visualizationQuery.showColumn.columnType === EReportColumnTypes.aggregate) {
      amountType = BasicColumnTypes.Numeric;
    }
  }
  if (amountType === EReportColumnTypes.aggregate) {
    amountType = BasicColumnTypes.Numeric;
  }

  return columnFormatter(amountType);
};
